import { ApiError, NetworkError } from '../lib/api'

/** What a grower calls the fields a save can reject, by the name the server reports. */
const FIELD_NAMES: Record<string, string> = {
  quantityHarvested: 'the amount',
  quantity: 'the amount',
  quantityUsed: 'the amount used',
  rateApplied: 'the rate',
  rateAmount: 'the rate',
  areaTreated: 'the area treated',
  phiDays: 'the days before harvest',
}

/** "the amount: must be at most 999,999.9999", from a 422's first field issue we can name. */
function fieldProblem(details: unknown): string | null {
  if (!Array.isArray(details)) return null
  for (const issue of details as { path?: string; message?: string }[]) {
    const name = FIELD_NAMES[issue.path?.split('.').pop() ?? '']
    if (name && issue.message) return `${name} ${issue.message}`
  }
  return null
}

/**
 * What to tell a grower when saving fails, instead of the server's developer-facing text. Only a
 * request that got no answer is "no connection"; a server fault, a refusal and a bug in our own
 * code each read differently. Conflict messages (draft limits, "already recorded") are written for
 * people and are shown as they are.
 */
export function problemMessage(err: unknown, action = 'record this entry'): string {
  if (err instanceof NetworkError) return `Couldn't ${action}: no connection. Your entry is kept on this device, so try again when you're back online.`
  if (!(err instanceof ApiError)) return `Couldn't ${action}. Please try again.`
  if (err.status >= 500) return `Couldn't ${action}: something went wrong on our side. Your entry is kept, so try again in a moment.`
  switch (err.code) {
    case 'PERMISSION_DENIED':
      return `Your role can't ${action} on this farm. Ask the farm's owner if you need to.`
    case 'VALIDATION_ERROR': {
      const field = fieldProblem(err.details)
      return field
        ? `Couldn't ${action}: ${field}. Use "Change" above to fix it.`
        : `Couldn't ${action}: something in it isn't valid. Go back over the steps and check the details.`
    }
    case 'NOT_FOUND':
      return `Couldn't ${action}: it may have been discarded or recorded elsewhere.`
    case 'CONFLICT':
      return err.message
    default:
      return `Couldn't ${action}. Please try again.`
  }
}
